import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BarChart3,
  User,
  Target,
  Briefcase,
  Rocket,
  Megaphone,
  Calculator,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  projectsApi,
  marketAnalysesApi,
  personasApi,
  positioningsApi,
  businessModelsApi,
  mvpPlansApi,
  marketingStrategiesApi,
  financialModelsApi,
} from '@/utils/api-pb';
import type { Project } from '@/utils/pocketbase';
import { extractPersonaName } from '@/utils/markdown';

import { useTranslation } from 'react-i18next';
export interface ArtifactAttachment {
  id: string;
  type: 'market_analysis' | 'persona' | 'positioning' | 'business_model' | 'mvp_plan' | 'marketing_strategy' | 'financial_model';
  label: string;
}

interface ArtifactItem {
  id: string;
  type: ArtifactAttachment['type'];
  label: string;
}

interface ArtifactGroup {
  type: ArtifactAttachment['type'];
  label: string;
  icon: typeof BarChart3;
  items: ArtifactItem[];
}

interface ArtifactSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (artifacts: ArtifactAttachment[]) => void;
  alreadySelected: ArtifactAttachment[];
}

export default function ArtifactSelectorDialog({
  open,
  onOpenChange,
  onConfirm,
  alreadySelected,
}: ArtifactSelectorDialogProps) {
  const { t } = useTranslation('chat');
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [groups, setGroups] = useState<ArtifactGroup[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingArtifacts, setIsLoadingArtifacts] = useState(false);

  // Load projects on open
  useEffect(() => {
    if (!open) return;
    setIsLoadingProjects(true);
    projectsApi.list()
      .then((p) => {
        setProjects(p);
        if (p.length === 1 && p[0]) {
          setSelectedProjectId(p[0].id);
        }
      })
      .catch(console.error)
      .finally(() => setIsLoadingProjects(false));
  }, [open]);

  // Initialize selected from already attached
  useEffect(() => {
    if (open) {
      setSelected(new Set(alreadySelected.map((a) => `${a.type}:${a.id}`)));
    }
  }, [open, alreadySelected]);

  // Load artifacts when project changes
  useEffect(() => {
    if (!selectedProjectId) {
      setGroups([]);
      return;
    }
    setIsLoadingArtifacts(true);

    Promise.all([
      marketAnalysesApi.list(selectedProjectId),
      personasApi.list(selectedProjectId),
      positioningsApi.list(selectedProjectId),
      businessModelsApi.list(selectedProjectId),
      mvpPlansApi.list(selectedProjectId),
      marketingStrategiesApi.list(selectedProjectId),
      financialModelsApi.list(selectedProjectId),
    ])
      .then(([marketAnalyses, personas, positionings, businessModels, mvpPlans, marketingStrategies, financialModels]) => {
        const newGroups: ArtifactGroup[] = [];

        const maItems = marketAnalyses
          .filter((a) => a.generated_text?.trim())
          .map((a, i, arr) => ({
            id: a.id,
            type: 'market_analysis' as const,
            label: arr.length > 1 ? `${t('artifactSelector.item.marketAnalysis')} (${i + 1})` : t('artifactSelector.item.marketAnalysis'),
          }));
        if (maItems.length > 0) {
          newGroups.push({ type: 'market_analysis', label: t('artifactSelector.group.marketAnalysis'), icon: BarChart3, items: maItems });
        }

        const pItems = personas
          .filter((a) => a.generated_text?.trim())
          .map((a) => ({
            id: a.id,
            type: 'persona' as const,
            label: extractPersonaName(a),
          }));
        if (pItems.length > 0) {
          newGroups.push({ type: 'persona', label: t('artifactSelector.group.personas'), icon: User, items: pItems });
        }

        const posItems = positionings
          .filter((a) => a.generated_text?.trim())
          .map((a, i, arr) => ({
            id: a.id,
            type: 'positioning' as const,
            label: arr.length > 1 ? `${t('artifactSelector.item.positioning')} (${i + 1})` : t('artifactSelector.item.positioning'),
          }));
        if (posItems.length > 0) {
          newGroups.push({ type: 'positioning', label: t('artifactSelector.group.positioning'), icon: Target, items: posItems });
        }

        const bmItems = businessModels
          .filter((a) => a.generated_text?.trim())
          .map((a, i, arr) => ({
            id: a.id,
            type: 'business_model' as const,
            label: arr.length > 1 ? `${t('artifactSelector.item.businessModel')} (${i + 1})` : t('artifactSelector.item.businessModel'),
          }));
        if (bmItems.length > 0) {
          newGroups.push({ type: 'business_model', label: t('artifactSelector.group.businessModels'), icon: Briefcase, items: bmItems });
        }

        const mvpItems = mvpPlans
          .filter((a) => a.generated_text?.trim())
          .map((a, i, arr) => ({
            id: a.id,
            type: 'mvp_plan' as const,
            label: arr.length > 1 ? `${t('artifactSelector.item.mvpPlan')} (${i + 1})` : t('artifactSelector.item.mvpPlan'),
          }));
        if (mvpItems.length > 0) {
          newGroups.push({ type: 'mvp_plan', label: t('artifactSelector.group.mvpPlans'), icon: Rocket, items: mvpItems });
        }

        const msItems = marketingStrategies
          .filter((a) => a.generated_text?.trim())
          .map((a, i, arr) => ({
            id: a.id,
            type: 'marketing_strategy' as const,
            label: arr.length > 1 ? `${t('artifactSelector.item.marketingStrategy')} (${i + 1})` : t('artifactSelector.item.marketingStrategy'),
          }));
        if (msItems.length > 0) {
          newGroups.push({ type: 'marketing_strategy', label: t('artifactSelector.group.marketingStrategy'), icon: Megaphone, items: msItems });
        }

        const fmItems = financialModels
          .filter((a) => a.ai_recommendations?.trim())
          .map((a, i, arr) => ({
            id: a.id,
            type: 'financial_model' as const,
            label: arr.length > 1 ? `${t('artifactSelector.item.financialModel')} (${i + 1})` : t('artifactSelector.item.financialModel'),
          }));
        if (fmItems.length > 0) {
          newGroups.push({ type: 'financial_model', label: t('artifactSelector.group.financialModel'), icon: Calculator, items: fmItems });
        }

        setGroups(newGroups);
      })
      .catch(console.error)
      .finally(() => setIsLoadingArtifacts(false));
  }, [selectedProjectId]);

  const toggleItem = (type: string, id: string) => {
    const key = `${type}:${id}`;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const artifacts: ArtifactAttachment[] = [];
    for (const group of groups) {
      for (const item of group.items) {
        if (selected.has(`${item.type}:${item.id}`)) {
          artifacts.push({ id: item.id, type: item.type, label: item.label });
        }
      }
    }
    onConfirm(artifacts);
    onOpenChange(false);
  };

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setSelectedProjectId('');
      setGroups([]);
      setSelected(new Set());
    }
  }, [open]);

  const totalArtifacts = groups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col rounded-[24px]">
        <DialogHeader>
          <DialogTitle className="font-manrope font-semibold text-[20px] leading-[28px] text-[#0d0d0d]">
            {t('artifactSelector.title')}
          </DialogTitle>
          <DialogDescription className="font-segoe text-[14px] leading-[20px] text-[#565858]">
            {t('artifactSelector.subtitle')}
          </DialogDescription>
        </DialogHeader>

        {/* Project selector */}
        <div>
          <label className="block font-manrope font-semibold text-[14px] leading-[20px] text-[#0d0d0d] mb-[8px]">
            {t('artifactSelector.labelProject')}
          </label>
          {isLoadingProjects ? (
            <div className="flex items-center gap-2 py-3 text-[#565858]">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="font-segoe text-[14px]">{t('artifactSelector.loading')}</span>
            </div>
          ) : projects.length === 0 ? (
            <p className="font-segoe text-[14px] leading-[20px] text-[#565858] py-2">
              {t('artifactSelector.noProjects')}
            </p>
          ) : (
            <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
              <SelectTrigger>
                <SelectValue placeholder={t('artifactSelector.placeholderProject')} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Artifacts list */}
        {selectedProjectId && (
          <div className="flex-1 overflow-y-auto min-h-0">
            {isLoadingArtifacts ? (
              <div className="flex items-center gap-2 py-6 justify-center text-[#565858]">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="font-segoe text-[14px]">{t('artifactSelector.loadingArtifacts')}</span>
              </div>
            ) : totalArtifacts === 0 ? (
              <p className="font-segoe text-[14px] leading-[20px] text-[#565858] py-4 text-center">
                {t('artifactSelector.noArtifacts')}
              </p>
            ) : (
              <div className="space-y-[16px]">
                {groups.map((group) => (
                  <div key={group.type}>
                    <div className="flex items-center gap-[8px] mb-[8px]">
                      <group.icon className="h-4 w-4 text-[#2197f1]" />
                      <span className="font-manrope font-semibold text-[14px] leading-[20px] text-[#0d0d0d]">
                        {group.label}
                      </span>
                    </div>
                    <div className="space-y-[4px]">
                      {group.items.map((item) => {
                        const key = `${item.type}:${item.id}`;
                        const isSelected = selected.has(key);
                        return (
                          <button
                            key={item.id}
                            onClick={() => toggleItem(item.type, item.id)}
                            className={cn(
                              'w-full flex items-center gap-[12px] px-[12px] py-[10px] rounded-[12px] text-left transition-colors',
                              isSelected
                                ? 'bg-[rgba(33,151,241,0.1)] border border-[#2197f1]'
                                : 'border border-[#eeeeee] hover:border-[#2197f1]'
                            )}
                          >
                            <div
                              className={cn(
                                'w-[20px] h-[20px] rounded-[4px] border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                                isSelected ? 'bg-[#2197f1] border-[#2197f1]' : 'border-[#c4c4c4]'
                              )}
                            >
                              {isSelected && (
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                  <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </div>
                            <span className="font-segoe text-[14px] leading-[20px] text-[#0d0d0d] truncate">
                              {item.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-[24px]">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="flex-1 bg-transparent border border-[var(--grey-accent,#eeeeee)] border-solid text-[color:var(--black-text,#0d0d0d)] px-[24px] py-[12px] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] hover:bg-[#2197f1] hover:text-white hover:border-[#2197f1] transition-colors text-center"
          >
            {t('artifactSelector.buttonCancel')}
          </button>
          {selectedProjectId && totalArtifacts > 0 && (
            <button
              onClick={handleConfirm}
              disabled={selected.size === 0}
              className={cn(
                'flex-1 py-[12px] rounded-[16px] font-[\'Segoe_UI\'] font-semibold text-[16px] leading-[24px] transition-opacity',
                selected.size > 0
                  ? 'bg-[#2197f1] text-white hover:opacity-90'
                  : 'bg-[rgba(33,151,241,0.2)] text-white cursor-not-allowed'
              )}
            >
              {t('artifactSelector.buttonAttach', { count: selected.size })}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
